import { useRef } from 'react';
import { Button } from '@sixui/ui';
import { focusRingTokens } from '@sixui/ui/components/FocusRing/FocusRing.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IMaybeAsync } from '~/helpers/types';
import { DropZonePlaceholder } from './DropZonePlaceholder';

export type IDropZoneRenderProps = {
  active?: boolean;
};

export type IDropZoneProps = IContainerProps & {
  children?:
    | React.ReactNode
    | ((props: IDropZoneRenderProps) => React.ReactNode);
  disabled?: boolean;
  active?: boolean;
  onClick?: () => IMaybeAsync<IAny>;
};

const styles = stylex.create({
  host: {
    display: 'flex',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colorSchemeTokens.outlineVariant,
    borderRadius: 'inherit',
    color: colorSchemeTokens.onSurface,
  },
  host$active: {
    borderColor: colorSchemeTokens.primary,
    backgroundColor: colorSchemeTokens.primaryContainer,
    color: colorSchemeTokens.onPrimaryContainer,
    pointerEvents: 'none',
  },
  host$disabled: {
    pointerEvents: 'none',
  },
  button: {
    textWrap: 'unset',
    padding: 0,
  },
});

const buttonFocusRingStyles = stylex.create({
  host: {
    [focusRingTokens.shape]: shapeTokens.corner$xs,
  },
});

const DropZone: React.FC<IDropZoneProps> = ({
  sx,
  children,
  disabled,
  active,
  onClick,
}) => {
  const dropZoneRef = useRef<HTMLDivElement | null>(null);

  const actionable = !disabled && !active && !!onClick;

  const renderContent = (): React.ReactNode =>
    typeof children === 'function' ? children({ active }) : children;

  return actionable ? (
    <Button
      variant="text"
      sx={[
        styles.host,
        styles.button,
        disabled ? styles.host$disabled : null,
        sx ?? null,
      ]}
      innerStyles={{
        focusRing: buttonFocusRingStyles,
      }}
      onClick={onClick}
    >
      {renderContent()}
    </Button>
  ) : (
    <div
      {...stylex.props(
        styles.host,
        disabled ? styles.host$disabled : null,
        active && styles.host$active,
        sx,
      )}
      ref={dropZoneRef}
    >
      {renderContent()}
    </div>
  );
};

const DropZoneNamespace = Object.assign(DropZone, {
  Placeholder: DropZonePlaceholder,
});

export { DropZoneNamespace as DropZone };
